import {Component, Input, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {NodeApiService} from '../../../../services/api/network/online/node/node-api.service';
import {RoutingInterfaceApiService} from '../../../../services/api/network/online/routing-interface/routing-interface-api.service';
import {HighchartComponent} from '../../../../utils/highchart/highchart.component';
import {StatisticsApiService} from '../../../../services/api/statistics/statistics-api.service';
import {ChartParentType} from '../../../../statistics/enums/chart-parent-type.enum';
import {ChartType} from '../../../../utils/highchart/chart-type';
import {ReportIdentifier} from '../../../../statistics/enums/report-identifier.enum';
import {DateType} from '../../../../statistics/enums/date-type.enum';
import {DateTimeUnitTypes} from '../../../../statistics/enums/date-time-unit-types.enum';
import {OverTimeGraphType} from '../../../../statistics/enums/over-time-graph-type.enum';
import {ReportFilterType} from '../../../../statistics/enums/report-filter-type.enum';
import {StatisticsTypes} from '../../../../statistics/enums/statistics-types.enum';
import {takeWhile} from 'rxjs/operators';
import {DataCacheApiService} from '../../../services/map/data-cache-api/data-cache-api.service';
import * as _ from 'lodash';
import {ChartDialogComponent} from '../../../../dialogs/chart-dialog/chart-dialog.component';
import {AcDialogService} from 'ac-infra';

@Component({
  selector: 'connection-summary',
  templateUrl: './connection-summary.component.html',
  styleUrls: ['./connection-summary.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class ConnectionSummaryComponent implements OnInit {

  @Input()
  set selection(selection: any) {
   this.dataInitialization(selection);
  }

  constructor(private nodeApiService: NodeApiService,
              private routingInterfacesApiService: RoutingInterfaceApiService,
              private statisticsApi: StatisticsApiService,
              private dataCacheApiService: DataCacheApiService,
              private acDialogService: AcDialogService) {
  }

  optionsForCalls: any;
  connectionStatistics: any;
  connection: any;
  isQualityEnabled: boolean;
  dataReady = false;
  ipProfiles = [];
  mediaRealm = [];

  @ViewChild(HighchartComponent, {static: false})
  chart: HighchartComponent;

  ngOnInit(): void {
    this.optionsForCalls = {
      parentType: ChartParentType.LINE,
      type: ChartType[ChartParentType.LINE].type,
      stacked: ChartType[ChartParentType.LINE].stack,
      series: [],
      title: {
        text: 'Calls Per 5 Minutes',
        fontSize: '12px'
      },
      subTitle: '',
      startDate: undefined,
      interval: 0,
      animation: true,
      exporting: false
    };

    this.dataCacheApiService.getDataReady().pipe(takeWhile(() => this.dataReady === false)).
    subscribe(x => {
      if (x === true) {
        this.dataReady = true;
        this.ipProfiles = this.dataCacheApiService.getIpProfiles();
        this.mediaRealm = this.dataCacheApiService.getMediaRealm();
        this.initializeQualityBasedRoutingConfigurations();
      }
    });
  }

  openCallsChartDialog(): void {
    this.acDialogService.open(ChartDialogComponent, {
      dialogData: this.optionsForCalls
    });
  }

  getChartsData(): void {
    if (this.connection === undefined || this.connection === null) {
      return;
    }
    this.getCallsReport();
  }

  getCallsReport(): void {
    const parameters = {
      reportIdentifier: ReportIdentifier.connectionOverTime,
      dateType: DateType.RELATIVE,
      relativeTimeUnit: DateTimeUnitTypes.HOUR,
      relativeTimeUnitValue: 3,
      overTimeGraphType: OverTimeGraphType.MULTIPLE_STATISTICS,
      availableFilters: [
        ReportFilterType.DATE,
        ReportFilterType.MULTIPLE_STATISTICS,
        ReportFilterType.ELEMENTS,
        ReportFilterType.OVER_TIME_GRAPH_TYPE
      ],
      multipleStatistics: [StatisticsTypes.TRANSIENT_CALL],
      elements: [this.connection.id]
    };

    this.statisticsApi.getReportForChart(parameters)
      .then((value) => {
        this.optionsForCalls.series = value.series;
        this.optionsForCalls.startDate = value.startDate;
        this.optionsForCalls.interval = value.interval;

        this.chart.setOptions(this.optionsForCalls);
      }).catch(() => {
    });
  }

  initializeQualityBasedRoutingConfigurations(): void {
    this.isQualityEnabled = this.dataCacheApiService.getQualityBasedRouting().isQualityEnabled;
  }

  isEmptyArray(item: any): boolean {
    return _.isArray(item) && item.length > 0;
  }

  scopeDestroyForBroadcasts = () => {};

  addIpProfilesData(): void {
    if (this.isEmptyArray(this.ipProfiles)) {
      if (!_.isNil(this.connection.connections[0])) {
        const ipProfileSrc = this.ipProfiles.find(ipProfile =>
            ipProfile.id === this.connection.connections[0].ipProfileId);
        this.connection.connections[0].ipProfileName = ipProfileSrc ? ipProfileSrc.name : '';
      }

      if (!_.isNil(this.connection.connections[1])) {
        const ipProfileDest = this.ipProfiles.find(ipProfile =>
            ipProfile.id === this.connection.connections[1].ipProfileId);
        this.connection.connections[1].ipProfileName = ipProfileDest ? ipProfileDest.name : '';
      }
    }
  }

  addMediaRealmData(): void {
    if (this.isEmptyArray(this.mediaRealm)) {
      if (!_.isNil(this.connection.connections[0])) {
        const mediaRealmSrc = this.mediaRealm.find(mediaRealmItem =>
            mediaRealmItem.id === this.connection.connections[0].mediaRealmId);
        this.connection.connections[0].mediaRealmName = mediaRealmSrc ? mediaRealmSrc.name : '';
      }

      if (!_.isNil(this.connection.connections[1])) {
        const mediaRealmDest = this.mediaRealm.find(mediaRealmItem =>
            mediaRealmItem.id === this.connection.connections[1].mediaRealmId);
        this.connection.connections[1].mediaRealmName = mediaRealmDest ? mediaRealmDest.name : '';
      }
    }
  }

  private dataInitialization(selection: any): void {
    if (_.isNil(selection)) { return; }

    const shapes = selection.nodes;
    const connections = selection.edges;

    this.connectionStatistics = [];
    if (shapes.length === 0 && connections.length === 1 && connections[0].connectionType === 'connection') {
      this.connection = connections[0].dataItem;
      this.connection.operState = this.connection.connections[0].operState === 'UNAVAILABLE' ||
      (this.connection.connections.length === 2 && this.connection.connections[1].operState === 'UNAVAILABLE') ? 'UNAVAILABLE' :
        'AVAILABLE';

      this.routingInterfacesApiService.getRoutingInterface(this.connection.connections[0].srcRoutingInterface)
        .then(fetchedRoutingInterface => {
          this.connection.connections[0].srcRoutingInterfaceName = fetchedRoutingInterface.operativeState === 'PREDELETED' ?
            fetchedRoutingInterface.name + ' (PREDELETED)' : fetchedRoutingInterface.name;
        });

      this.nodeApiService.getNode(this.connection.connections[0].srcNodeId).then(fetchedNode => {
        this.connection.connections[0].srcNodeName = fetchedNode.name;
      });

      if (this.connection.type !== 'NODE_TO_THIRD_PARTY') {
        this.routingInterfacesApiService.getRoutingInterface(this.connection.connections[1].srcRoutingInterface)
          .then(fetchedRoutingInterface => {
            this.connection.connections[1].srcRoutingInterfaceName = fetchedRoutingInterface.operativeState === 'PREDELETED' ?
              fetchedRoutingInterface.name + ' (PREDELETED)' : fetchedRoutingInterface.name;
          });

        this.nodeApiService.getNode(this.connection.connections[1].srcNodeId).then(fetchedNode => {
          this.connection.connections[1].srcNodeName = fetchedNode.name;
        });
      }

      this.addIpProfilesData();
      this.addMediaRealmData();

      this.getChartsData();
    }
  }
}
